import { Nullable } from "common/types";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { fromJS, Map, Record } from "immutable";

export type CustomFormKind = "job_resulting" | "unknown";

/** date-fns format for timezone-bearing RJSF `date`/`date-time` fields (e.g. "2026-05-29T00:00:00-0500"). */
export const RJSF_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXX";
/** date-fns format for RJSF `time` fields (e.g. "13:14:00"). */
export const RJSF_TIME_FORMAT = "HH:mm:ss";

export const customFormFromJSON = (json: Partial<ICustomFormData>): CustomFormRecord => {
  const record: ICustomForm = { ...(json as any) };

  if (json.created_at) {
    record.created_at = new Date(json.created_at);
  }

  if (json.form_data) {
    record.form_data = fromJS(json.form_data);
  }

  if (json.id === "0") {
    record.id = -1;
  }

  if (json.kind !== undefined && json.kind !== "job_resulting") {
    record.kind = "unknown";
  }

  if (json.updated_at) {
    record.updated_at = new Date(json.updated_at);
  }

  return new CustomFormRecord(record);
};

export const customFormToJSON = (record: CustomFormRecord): any => {
  const data: Partial<ICustomFormData> = {
    form_data: record.form_data.toJS(),
    job_uuid: record.job_uuid,
    kind: record.kind,
  };

  if (record.id !== -1) {
    data.id = record.id;
  }

  return data;
};

/**
 * Serializes an RJSF date/time picker value to the string its JSON-schema `format` expects, so the
 * RJSF-rendered custom form (job resulting) keeps `form_data` string-typed end-to-end. Specific to
 * the RJSF path — the DaBella custom form and other non-RJSF custom forms don't use this. Matches the
 * submit-time transform in `validateFormData`; without it the picker writes a `Date` that never
 * structurally equals the hydrated string, leaving the form forever dirty.
 *
 * @param date - the picked date, or undefined when cleared
 * @param schemaFormat - the RJSF (JSON-schema) `format` of the field ("date" | "time" | "date-time")
 * @returns the serialized string, or undefined when no date is set
 */
export const serializeRjsfDate = (date: Date | undefined, schemaFormat?: string): string | undefined => {
  if (!date) {
    return undefined;
  }

  if (schemaFormat === "time") {
    return format(date, RJSF_TIME_FORMAT);
  }

  return formatInTimeZone(date, Intl.DateTimeFormat().resolvedOptions().timeZone, RJSF_DATETIME_FORMAT);
};

export interface ICustomFormData {
  id: string | -1;

  created_by_uuid: string;
  form_data: any;
  job_uuid: string;
  kind: string;

  created_at: Nullable<string>;
  loading: boolean;
  updated_at: Nullable<string>;
}

export interface ICustomForm {
  id: string | -1;

  created_by_uuid: string;
  form_data: Map<string, any>;
  job_uuid: string;
  kind: CustomFormKind;

  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
  loading: boolean;
}

export const defaultCustomFormProps: ICustomForm = {
  id: -1,

  created_by_uuid: "",
  form_data: Map(),
  job_uuid: "",
  kind: "job_resulting",

  created_at: null,
  updated_at: null,
  loading: false,
};

export class CustomFormRecord extends Record<ICustomForm>(defaultCustomFormProps) implements ICustomForm {}
